import { CommonModule } from '@angular/common';
import {
  Component,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CtaButtonComponent } from '../../../../../shared/components/ui/cta-button/cta-button.component';
import { UploadMediaComponent } from './upload-media/upload-media.component';
import { MainInfoComponent } from './main-info/main-info.component';
import { StructureComponent } from './structure/structure.component';
import { VideoHeroesSettingsComponent } from './video-heroes/video-heroes-settings.component';
import { RecommendationsBoxComponent } from './recommendations-box/recommendations-box.component';
import { TranslatePipe } from '@ngx-translate/core';
import { CourseSettingsData } from '../../../models';

@Component({
  selector: 'app-course-settings',
  standalone: true,
  imports: [
    CommonModule,
    CtaButtonComponent,
    UploadMediaComponent,
    MainInfoComponent,
    StructureComponent,
    VideoHeroesSettingsComponent,
    RecommendationsBoxComponent,
    TranslatePipe,
  ],
  templateUrl: './course-settings.component.html',
  styleUrl: './course-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseSettingsComponent {
  /** `school` = uploads + POST /courses; `admin` = topic + POST /courses/valvur. */
  layoutVariant = input<'school' | 'admin'>('school');

  stepCompleted = output<void>();
  settingsDataChange = output<CourseSettingsData>();

  // Data from child components
  private uploadMediaData = signal<{ files: File[] }>({ files: [] });
  private mainInfoData = signal<{
    discipline: string;
    audience: string;
    tone: string;
    topic: string;
  }>({
    discipline: '',
    audience: '',
    tone: '',
    topic: '',
  });
  private structureData = signal<{ quantity: string; duration: string }>({
    quantity: 'M',
    duration: 'M-3-5',
  });
  private videoHeroesData = signal<{
    videoMainHeroName: string;
    videoMainHeroDescription: string;
    videoBackgroundHeroNames: string[];
  }>({
    videoMainHeroName: '',
    videoMainHeroDescription: '',
    videoBackgroundHeroNames: [],
  });

  // Combined settings data
  settingsData = computed<CourseSettingsData>(() => {
    const isAdmin = this.layoutVariant() === 'admin';
    const main = this.mainInfoData();
    const structure = this.structureData();
    const heroes = this.videoHeroesData();
    const base: CourseSettingsData = {
      files: isAdmin ? [] : this.uploadMediaData().files,
      discipline: main.discipline,
      audience: main.audience,
      tone: main.tone,
      quantity: structure.quantity,
      duration: structure.duration,
    };
    if (isAdmin) {
      return {
        ...base,
        topic: main.topic.trim(),
        videoMainHeroName: heroes.videoMainHeroName,
        videoMainHeroDescription: heroes.videoMainHeroDescription,
        videoBackgroundHeroNames: [...heroes.videoBackgroundHeroNames],
      };
    }
    return base;
  });

  // Handlers for child component data changes
  // Only collect data, don't emit until Complete is clicked
  onUploadMediaChange(data: { files: File[] }): void {
    this.uploadMediaData.set(data);
  }

  onMainInfoChange(data: {
    discipline: string;
    audience: string;
    tone: string;
    topic?: string;
  }): void {
    this.mainInfoData.set({
      discipline: data.discipline,
      audience: data.audience,
      tone: data.tone,
      topic: data.topic ?? '',
    });
  }

  onStructureChange(data: { quantity: string; duration: string }): void {
    this.structureData.set(data);
  }

  onVideoHeroesChange(data: {
    videoMainHeroName: string;
    videoMainHeroDescription: string;
    videoBackgroundHeroNames: string[];
  }): void {
    this.videoHeroesData.set(data);
  }

  onComplete(): void {
    if (this.layoutVariant() === 'admin') {
      const topic = (this.mainInfoData().topic ?? '').trim();
      if (!topic) {
        return;
      }
    }
    // Emit collected data only when Complete is clicked
    this.settingsDataChange.emit(this.settingsData());
    this.stepCompleted.emit();
  }

  // Get current settings data (for external access)
  getCurrentSettingsData(): CourseSettingsData {
    return this.settingsData();
  }
}
